/**
 * `useCompanionNameStore` — Zustand slice over `AI_Companion_Name`.
 *
 * The persisted companion name is the gate for the Naming_Flow
 * (Requirement 16.1) and is injected into every System_Prompt via the
 * Prompt_Builder when present (Requirement 1.7). Changes propagate
 * across the UI without a reload (Requirement 16.7) — Settings'
 * Companion Name editor and the Naming_Flow both write through this
 * slice, and every component that displays the name subscribes to it.
 *
 * `null` is the explicit "no name persisted yet" state (Requirement
 * 16.1). The slice does NOT collapse empty strings to `null` here —
 * the gate predicate (`isNamingFlowActive`) handles that — so the
 * UI's "what was last persisted" surface is faithful to the store.
 */

import { create } from 'zustand';

import { getActiveConversationStore } from './storeContext';

/** Public slice surface. */
export interface CompanionNameState {
  /** The persisted name, or `null` when none is set yet. */
  name: string | null;
  /** `true` once `hydrate()` has resolved. */
  hydrated: boolean;
  /** Read the persisted companion name. */
  hydrate(): Promise<void>;
  /** Write a new name (or `null` to clear) and persist atomically. */
  setName(name: string | null): Promise<void>;
}

export const useCompanionNameStore = create<CompanionNameState>((set) => ({
  name: null,
  hydrated: false,

  async hydrate(): Promise<void> {
    const store = getActiveConversationStore();
    const persisted = await store.getCompanionName();
    set({ name: persisted, hydrated: true });
  },

  async setName(name: string | null): Promise<void> {
    // Update local first so subscribers (UI labels, prompt builder)
    // see the new value within the same tick. Persistence follows
    // synchronously after the await.
    set({ name });
    await getActiveConversationStore().setCompanionName(name);
  },
}));
